import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as payrollService from '../../services/payrollService';
import useUIStore from '../../store/uiStore';
import ScrollX from '../../components/ScrollX';
import PayrollTabs from '../../components/PayrollTabs';

const MONTHS = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const now = new Date();

const STATUS_STYLE = {
  draft: 'bg-gray-100 text-gray-700',
  finalized: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
};

const PayrollRunsPage = () => {
  const navigate = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ month: now.getMonth() + 1, year: now.getFullYear(), branch: '' });
  const [submitting, setSubmitting] = useState(false);
  const showToast = useUIStore((s) => s.showToast);

  const load = () => {
    setLoading(true);
    payrollService.getRuns()
      .then((res) => setRuns(res.data.runs || []))
      .catch((err) => showToast(err.response?.data?.detail || 'Failed to load payroll runs', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await payrollService.createRun({
        month: Number(form.month),
        year: Number(form.year),
        branch: form.branch || null,
      });
      showToast('Payroll run created', 'success');
      setShowCreate(false);
      navigate(`/payroll/runs/${res.data.run.id}`);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to create payroll run', 'error');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <PayrollTabs />

        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-semibold text-white">Payroll Runs</h2>
          <div className="flex gap-2">
            <button onClick={() => navigate('/payroll/import')} className="px-4 py-2 text-sm rounded border border-white/40 text-white hover:bg-white/10 font-semibold">
              Load Old Salary Sheet
            </button>
            <button onClick={() => setShowCreate((v) => !v)} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold">
              {showCreate ? 'Cancel' : '+ New Payroll Run'}
            </button>
          </div>
        </div>

        {showCreate && (
          <form onSubmit={handleCreate} className="glass-card p-6 mb-6 flex items-end gap-4 flex-wrap">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
              <select value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="border border-gray-300 rounded px-3 py-2 text-sm">
                {MONTHS.slice(1).map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="w-24 border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Branch (optional)</label>
              <input value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })} placeholder="FACTORY / HO / leave blank for all" className="border border-gray-300 rounded px-3 py-2 text-sm" />
            </div>
            <button type="submit" disabled={submitting} className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60">
              {submitting ? 'Creating...' : 'Create Run'}
            </button>
          </form>
        )}

        <div className="glass-card">
          {loading ? (
            <p className="text-gray-500 text-sm p-5">Loading...</p>
          ) : (
            <ScrollX>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100">
                    <th className="px-4 py-2 font-medium">Period</th>
                    <th className="px-4 py-2 font-medium">Branch</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Finalized</th>
                    <th className="px-4 py-2 font-medium">Paid</th>
                    <th className="px-4 py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r) => (
                    <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/payroll/runs/${r.id}`)}>
                      <td className="px-4 py-3 font-medium text-gray-900">{MONTHS[r.month]} {r.year}</td>
                      <td className="px-4 py-3">{r.branch || 'All branches'}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-700'}`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{r.finalized_at ? new Date(r.finalized_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td className="px-4 py-3 text-gray-500">{r.paid_at ? new Date(r.paid_at).toLocaleDateString('en-IN') : '—'}</td>
                      <td className="px-4 py-3 text-komal-blue font-semibold text-xs">Open →</td>
                    </tr>
                  ))}
                  {runs.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-400">No payroll runs yet.</td></tr>
                  )}
                </tbody>
              </table>
            </ScrollX>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayrollRunsPage;
